import{occasionLabels as sharedOccasionLabels}from'./labels';
import { z } from 'zod';
import { budgetOptions, occasionOptions, relationOptions, urgencyOptions, type Brief, type Budget, type Occasion, type Relation } from './domain';
import {ageBands,parseAgeBand} from './age-recommendation';

export const ParsedGiftIntentSchema=z.object({
  relation:z.enum(relationOptions).optional(),
  occasion:z.enum(occasionOptions).optional(),
  budget:z.enum(budgetOptions).optional(),
  budgetMax:z.number().int().positive().max(10_000_000).optional(),
  ageBand:z.enum(ageBands).optional(),
  interests:z.array(z.string()).max(3),
  avoids:z.array(z.string()).max(4),
  owned:z.array(z.string()).max(4),
  urgency:z.enum(urgencyOptions).optional(),
  wantsCurrent:z.boolean(),
  source:z.literal('local_rules'),
  missing:z.array(z.enum(['relation','occasion','budget'])),
});
export type ParsedGiftIntent=z.infer<typeof ParsedGiftIntentSchema>;

type Dictionary<T extends string>={value:T;terms:readonly string[]};
const relationDictionary:readonly Dictionary<Relation>[]= [
  {value:'partner',terms:['남자친구','여자친구','남친','여친','연인','배우자','남편','아내']},
  {value:'child',terms:['아들','딸','아이','어린이','조카','초등학생','중학생','고등학생']},
  {value:'teacher',terms:['선생님','스승','교수님','멘토']},
  {value:'coworker',terms:['직장 동료','회사 동료','동료','상사','팀장','부장','신입','퇴사자']},
  {value:'family',terms:['엄마','어머니','아빠','아버지','부모님','언니','오빠','누나','형','동생','가족']},
  {value:'friend',terms:['친구','지인','친한 사람']},
];
const occasionDictionary:readonly Dictionary<Occasion>[]= [
  {value:'anniversary',terms:['결혼기념일','결혼 기념일']},
  {value:'farewell',terms:['퇴사','이직','송별']},
  {value:'housewarming',terms:['집들이','이사','새집']},
  {value:'comfort',terms:['위로','회복','퇴원','힘들어','아파','지쳤']},
  {value:'thanks',terms:['감사','고마움','답례','도와줘서']},
  {value:'holiday',terms:['명절','설날','추석','연말']},
  {value:'celebrate',terms:['승진','취업','합격','졸업','결혼','출산','개업','축하']},
  {value:'birthday',terms:['생일','돌잔치']},
];
export const interestSignalOptions=['coffee','tea','food','books','art','movie','craft','memory','plants','care','sleep','office','tech','writing','fitness','outdoor','travel','practical']as const;
export const avoidSignalOptions=['allergy','caffeine','fragrance','skincare','pet','size','owned','schedule','mobility','privacy','pollen','boundary']as const;

const interestDictionary:readonly Dictionary<(typeof interestSignalOptions)[number]>[]= [
  {value:'coffee',terms:['커피','원두','드립백','카페']},
  {value:'tea',terms:['차를 좋아','차 좋아','차와','티를 좋아','홍차','녹차','허브티']},
  {value:'food',terms:['먹는 걸 좋아','맛집','디저트','과일','빵을 좋아','요리']},
  {value:'books',terms:['책을 좋아','책 좋아','책과','책을','독서','서점','책 읽']},
  {value:'art',terms:['전시','미술','그림','공연']},
  {value:'movie',terms:['영화','극장','시네마']},
  {value:'craft',terms:['만들기','공예','뜨개','도예','클래스']},
  {value:'memory',terms:['추억','사진','기념']},
  {value:'plants',terms:['식물','화분','가드닝']},
  {value:'care',terms:['뷰티','화장품','스킨케어','돌봄']},
  {value:'sleep',terms:['잠','수면','휴식']},
  {value:'office',terms:['회사','사무실','책상','업무']},
  {value:'tech',terms:['전자기기','기계','테크','가젯','충전']},
  {value:'writing',terms:['글쓰기','기록','다이어리','문구']},
  {value:'fitness',terms:['운동','헬스','러닝','요가','필라테스']},
  {value:'outdoor',terms:['캠핑','등산','야외','피크닉']},
  {value:'travel',terms:['여행','숙박','호캉스']},
  {value:'practical',terms:['실용','쓸모','필요한 것']},
];

const includesAny=(text:string,terms:readonly string[])=>terms.some(term=>text.includes(term));
const firstMatch=<T extends string>(text:string,dictionary:readonly Dictionary<T>[])=>dictionary.find(entry=>includesAny(text,entry.terms))?.value;
const negativeNear=(text:string,term:string)=>{
  const index=text.indexOf(term);if(index<0)return false;
  const around=text.slice(Math.max(0,index-8),Math.min(text.length,index+term.length+12));
  return /(싫|안\s*좋|못\s*먹|피하|제외|말고|금지)/.test(around);
};

function parseBudget(text:string):{budget?:Budget;budgetMax?:number}{
  const man=text.match(/(\d+(?:\.\d+)?)\s*만(?:\s*원)?(?:\s*(?:~|〜|-|에서|부터)\s*(\d+(?:\.\d+)?)\s*만)?/);
  const won=text.match(/(\d{4,7})\s*원/);
  const amount=man?Number(man[2]??man[1])*10_000:won?Number(won[1]):undefined;
  if(amount===undefined||!Number.isFinite(amount))return{};
  const budget=amount<20_000?'under20':amount<=50_000?'20to50':amount<=100_000?'50to100':'over100';
  const budgetMax=/(이내|안(?:쪽|으로)?|까지|넘지|이하)/.test(text)?Math.round(amount):undefined;
  return{budget,budgetMax};
}

function parseOwned(text:string){
  const ownership=/(이미|벌써|많이).{0,18}(있|가졌|보유|많)|.{0,12}(있|가졌|보유).{0,6}(많|충분)/;
  if(!ownership.test(text))return[];
  const marker=text.search(/이미|벌써|많이|보유|가졌|있(?:고|어|는|음)/);const scoped=marker<0?text:text.slice(Math.max(0,marker-14),Math.min(text.length,marker+34));
  const dictionary:readonly Dictionary<string>[]= [
    {value:'books',terms:['책','도서']},{value:'coffee',terms:['커피','원두','드립백']},{value:'tea',terms:['차','티백','홍차','녹차']},
    {value:'drinkware',terms:['머그','텀블러','컵']},{value:'office',terms:['문구','노트','다이어리','책상']},{value:'tech',terms:['충전기','보조배터리','전자기기','가젯']},
    {value:'plants',terms:['식물','화분']},{value:'care',terms:['화장품','핸드크림','스킨케어']},{value:'fitness',terms:['운동용품','요가용품']},
  ];
  return dictionary.filter(entry=>includesAny(scoped,entry.terms)).map(entry=>entry.value).slice(0,4);
}

function parseAvoids(text:string){
  const avoids:string[]=[];
  const add=(value:string,condition:boolean)=>{if(condition&&!avoids.includes(value)&&avoids.length<4)avoids.push(value)};
  add('allergy',/(알레르|못\s*먹|식이 제한)/.test(text));
  add('caffeine',/(카페인[^. ]*(싫|못|안)|커피.{0,8}(싫|못|안\s*마)|디카페인만)/.test(text));
  add('fragrance',/(향.{0,10}(싫|강한|피하|민감)|무향)/.test(text));
  add('skincare',/(피부.{0,10}(민감|예민|알레르)|화장품.{0,8}(싫|안\s*써))/.test(text));
  add('pet',/(반려동물|강아지|고양이).{0,10}(키우|있)/.test(text));
  add('size',/(사이즈|치수).{0,10}(몰라|모름|어려)/.test(text));
  add('schedule',/(시간.{0,8}(없|안\s*맞)|일정.{0,8}(어려|몰라))/.test(text));
  add('mobility',/(이동.{0,8}(어려|불편)|거동.{0,8}(불편|어려))/.test(text));
  add('privacy',/(사진.{0,8}(싫|부담)|사생활|공개.{0,8}(싫|부담))/.test(text));
  add('pollen',/(꽃가루|꽃.{0,8}(싫|알레르))/.test(text));
  add('boundary',/(선물.{0,8}(부담|싫)|부담스러운.{0,6}(호의|선물))/.test(text));
  return avoids;
}

export function parseGiftBrief(raw:string):ParsedGiftIntent{
  const text=raw.trim().normalize('NFC').toLocaleLowerCase('ko-KR').slice(0,700);
  const relation=firstMatch(text,relationDictionary);
  const occasion=firstMatch(text,occasionDictionary);
  const {budget,budgetMax}=parseBudget(text);
  const owned=parseOwned(text);
  const avoids=parseAvoids(text);
  if(owned.length===0&&/(이미.{0,12}(있|가졌|보유)|많이.{0,12}(있|가졌|보유))/.test(text))avoids.push('owned');
  const interests=interestDictionary.filter(entry=>includesAny(text,entry.terms)&&!entry.terms.some(term=>negativeNear(text,term))).map(entry=>entry.value).slice(0,3);
  const urgency:ParsedGiftIntent['urgency']=/(오늘|당일|내일)/.test(text)?'today':/(이번\s*주|주말|며칠)/.test(text)?'week':undefined;
  const wantsCurrent=/(요즘|최신|유행|인스타|인스타그램|sns|급상승|트렌드|핫한)/i.test(text);
  const missing=(['relation','occasion','budget']as const).filter(key=>({relation,occasion,budget})[key]===undefined);
  return ParsedGiftIntentSchema.parse({relation,occasion,budget,budgetMax,ageBand:parseAgeBand(text),interests,avoids:avoids.slice(0,4),owned,urgency,wantsCurrent,source:'local_rules',missing});
}

export function completeBrief(intent:ParsedGiftIntent,overrides:Partial<Brief>={}):Brief|null{
  const relation=overrides.relation??intent.relation;const occasion=overrides.occasion??intent.occasion;const budget=overrides.budget??intent.budget;
  if(!relation||!occasion||!budget)return null;
  return{relation,occasion,budget,budgetMax:overrides.budgetMax??intent.budgetMax,ageBand:overrides.ageBand??intent.ageBand,interests:overrides.interests??intent.interests,avoids:overrides.avoids??intent.avoids,owned:overrides.owned??intent.owned,urgency:overrides.urgency??intent.urgency,wantsCurrent:overrides.wantsCurrent??intent.wantsCurrent,recipientKey:overrides.recipientKey};
}

export const giftBriefExamples=[
  '인스타에서 요즘 보이는 센스 있는 동료 승진 선물, 5만원 안쪽. 향이 강한 건 싫어해요.',
  '책 좋아하는 친구 생일이에요. 3만원 정도, 이미 가진 물건은 피하고 싶어요.',
  '엄마가 요즘 지쳐 보여서 10만원 안으로 부담 없는 위로 선물을 찾고 있어요.',
]as const;

export function intentLabels(intent:ParsedGiftIntent){
  const labels:Record<string,string>={partner:'연인·배우자',family:'가족',friend:'친구',coworker:'동료',teacher:'스승·선생님',child:'아이',...sharedOccasionLabels,under20:'2만원 아래','20to50':'2만~5만원','50to100':'5만~10만원',over100:'10만원 이상',today:'오늘 필요',week:'이번 주 필요',flexible:'일정 여유',coffee:'커피',tea:'차',food:'먹는 것',books:'책',drinkware:'컵·텀블러',art:'예술',movie:'영화',craft:'만들기',memory:'추억',plants:'식물',care:'돌봄',sleep:'휴식',office:'일·책상',tech:'기술',writing:'기록',fitness:'운동',outdoor:'야외',travel:'여행',practical:'실용',allergy:'알레르기',caffeine:'카페인',fragrance:'강한 향',skincare:'민감 피부',pet:'반려동물',size:'사이즈',owned:'이미 있음',schedule:'일정',mobility:'이동',privacy:'사생활',pollen:'꽃가루',boundary:'선물 부담'};
  return [intent.relation,intent.occasion,intent.budget,intent.budgetMax?`max:${intent.budgetMax}`:undefined,intent.urgency,intent.wantsCurrent?'요즘 근거 반영':undefined,...intent.interests,...intent.owned.map(value=>`owned:${value}`),...intent.avoids.map(value=>`avoid:${value}`)].filter(Boolean).map(value=>String(value).startsWith('avoid:')?`${labels[String(value).slice(6)]??String(value).slice(6)} 제외`:String(value).startsWith('owned:')?`${labels[String(value).slice(6)]??String(value).slice(6)} 이미 있음`:String(value).startsWith('max:')?`총 ${Number(String(value).slice(4)).toLocaleString('ko-KR')}원 이내`:labels[String(value)]??String(value));
}

export const ModelGiftIntentSchema=z.object({
  relation:z.enum(relationOptions).nullable(),occasion:z.enum(occasionOptions).nullable(),budget:z.enum(budgetOptions).nullable(),
  interests:z.array(z.enum(interestSignalOptions)).max(3),avoids:z.array(z.enum(avoidSignalOptions)).max(4),ambiguities:z.array(z.string().max(80)).max(3),
}).strict();
export type ModelGiftIntent=z.infer<typeof ModelGiftIntentSchema>;

export function mergeModelIntent(local:ParsedGiftIntent,model:unknown):ParsedGiftIntent{
  const parsed=ModelGiftIntentSchema.safeParse(model);if(!parsed.success)return local;
  const relation=local.relation??parsed.data.relation??undefined;const occasion=local.occasion??parsed.data.occasion??undefined;const budget=local.budget??parsed.data.budget??undefined;
  const interests=[...new Set([...local.interests,...parsed.data.interests])].slice(0,3);
  const avoids=[...new Set([...local.avoids,...parsed.data.avoids])].slice(0,4);
  const missing=(['relation','occasion','budget']as const).filter(key=>({relation,occasion,budget})[key]===undefined);
  return ParsedGiftIntentSchema.parse({...local,relation,occasion,budget,interests,avoids,missing});
}
